import React, { useCallback, useRef, useState } from "react";
import { deleteChatMessage, editChatMessage } from "../../api/ProcurementClient";
import { ChatAttachment, ChatMessage } from "../../models/Chat";
import ConfirmDialog from "../ConfirmDialog";
import ChatAttachmentDialog from "./ChatAttachmentDialog";
import { ComposerSubmit } from "./Composer";
import { toggleChatReaction } from "./messageActions";

/**
 * Reaction / edit / delete / open-attachment handlers shared by the message pane
 * and the thread panel. Handlers are stable so memoized MessageItems don't re-render.
 */
export function useMessageActions(
  meId: number,
  onUpdated: (message: ChatMessage) => void,
  onError: (message: string) => void
) {
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [openAttachment, setOpenAttachment] = useState<ChatAttachment | null>(null);
  const onUpdatedRef = useRef(onUpdated);
  const onErrorRef = useRef(onError);
  onUpdatedRef.current = onUpdated;
  onErrorRef.current = onError;

  const errorText = (err: unknown, fallback: string) =>
    err instanceof Error ? err.message : fallback;

  const onToggleReaction = useCallback(
    (message: ChatMessage, emoji: string) => {
      toggleChatReaction(message, emoji, meId)
        .then((updated) => onUpdatedRef.current(updated))
        .catch((err) => onErrorRef.current(errorText(err, "Reaction failed")));
    },
    [meId]
  );

  const onEdit = useCallback(async (message: ChatMessage, input: ComposerSubmit) => {
    onUpdatedRef.current(await editChatMessage(message.id, input.body, input.mentionUserIds));
  }, []);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      onUpdatedRef.current(await deleteChatMessage(pendingDelete.id));
    } catch (err) {
      onErrorRef.current(errorText(err, "Failed to delete message"));
    } finally {
      setPendingDelete(null);
    }
  };

  const dialogs = (
    <>
      <ChatAttachmentDialog attachment={openAttachment} onClose={() => setOpenAttachment(null)} />
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete message?"
        okLabel="Delete"
        okColor="error"
        cancelColor="inherit"
        onOk={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      >
        This removes the message and its attachments for everyone.
      </ConfirmDialog>
    </>
  );

  return {
    onToggleReaction,
    onEdit,
    onDelete: setPendingDelete,
    onOpenAttachment: setOpenAttachment,
    dialogs,
  };
}
